import { forwardRef, memo, MutableRefObject, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import './index.scss'
import { useTranslation } from 'react-i18next'
import Input from '../../../components/Input'
import { useQuery } from 'react-query'
import { getMobileList, getTrailHistory } from '../../../services/calls/mobiles'
import Infobox from '../../../components/Infobox'
import Button from '../../../components/Button'
import Icon from '../../../components/Icon'
import { COLOR } from '../../../data/variables'
import { DTO_TrailStop, TrailHistoryEntry, TrailHistoryParam } from '../../../services/calls/types/mobiles'
import { DaterangeValue } from '../../../components/Input/Daterange'
import UserContext from '../../../contexts/UserContext/context'
import { TrailInfo, useMap } from '../../../hooks/useMap'
import { ICON } from '../../../components/Icon/data'
import useDynamicRef from '../../../hooks/useDynamicRef'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { FlotiMapRef } from '../../../components/FlotiMap/type'
import { InputError } from '../../../components/Input/types'
import { useSearchParams } from 'react-router-dom'

type HistorySidebar = {
  mapRef: MutableRefObject<FlotiMapRef>
  setTrailInfo: (data: Record<string, TrailInfo>) => void
}

const History__sidebar = memo(forwardRef(({
  mapRef, setTrailInfo,
}: HistorySidebar, ref) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [searchParams] = useSearchParams()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchBy, setSearchBy] = useState<string>('vehicle')
  const [searchFilter, setSearchFilter] = useState<string[] | number[]>(user?.preferences?.SELECTED_MOBILE ? [user.preferences.SELECTED_MOBILE] : [])
  const [dates, setDates] = useState<DaterangeValue>()
  const [error, setError] = useState<{ [key: string]: InputError }>({})

  const { data: mobiles } = useQuery(['getMobileList'], () => getMobileList())

  const { extractHistory } = useMap()

  const mobileLookup = useMemo(() => {
    const lookup = {}
    if (mobiles) {
      mobiles.forEach((mobile) => {
        lookup[mobile.id] = mobile.globalid
      })
    }
    return lookup
  }, [mobiles])

  const searchMap = {
    vehicle: t('Mobiles'),
    driver: t('Drivers')
  }

  const fetchHistory = useCallback(async () => {
    const tempErrors = {}
    if (!searchFilter.length) {
      tempErrors.mobiles = {
        error: 'error',
        message: t('Please select a mobile')
      }
    }
    setError(tempErrors)

    if (!Object.keys(tempErrors).length) {
      setIsLoading(true)
      try {
        const results = await Promise.all(
          searchFilter.map((mobile) => {
            const params: TrailHistoryParam = {
              mobileid: [mobile] as string[] | number[],
              from_datetime: dates!.from,
              to_datetime: dates!.to
            }
            return getTrailHistory(params)
          })
        )

        let trail_history: TrailHistoryEntry[] = []
        let stops: DTO_TrailStop = {}
        results.forEach((resp) => {
          if (resp) {
            trail_history = [...trail_history, ...resp.trail_history.trail_history]
            stops = { ...stops, ...resp.trail_history.stops }
          }
        })

        if (trail_history.length) {
          const mobileInfo = extractHistory(trail_history, stops)
          setTrailInfo(mobileInfo)
        }
      } finally {
        setIsLoading(false)
      }
    }
  }, [searchFilter, dates, extractHistory, setTrailInfo])

  useEffect(() => {
    const paramMobiles = searchParams.get('mobile')
    const paramFrom = searchParams.get('from')
    const paramTo = searchParams.get('to')
    if (paramFrom && paramTo) setDates({ from: paramFrom, to: paramTo })
    if (paramMobiles) {
      setSearchFilter(Array.isArray(paramMobiles) ? paramMobiles : [paramMobiles])
      fetchHistory()
    }
  }, [])

  const getFilters = useCallback(() => {
    return { dates, mobiles: searchFilter }
  }, [dates, searchFilter])

  const forwardRefAction = useDynamicRef({
    getFilters
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  const submitButtonLabel = useMemo(() => {
    if (isLoading) {
      return (
        <>
          {`${t('Loading')}...`}
          <LoadingSpinner color={COLOR.WHITE} />
        </>
      )
    }

    return (
      <>
        {t('Show history')}
        <Icon icon={ICON.SEARCH} color={COLOR.WHITE} />
      </>
    )
  }, [isLoading, t])

  return (
    <div className="History__sidebar">
      <div className="History__sidebar-title">{t('History')}</div>
      <div className="History__sidebar-subtitle">{t('Check the speed history of specific vehicle(s)')}</div>
      <div className="History__sidebar-filters">
        <div className="History__sidebar-filters__radio-label">{t('Search by')}</div>
        <div className="History__sidebar-filters__radio">
          <Input
            type="radio"
            name="sortby"
            size="s"
            valueKey="vehicle"
            label={t('Vehicle')}
            value={searchBy}
            onInput={(val) => setSearchBy(val)}
          />
          {false && <Input // hide for now
            type="radio"
            name="sortby"
            size="s"
            valueKey="driver"
            label={t('Driver')}
            value={searchBy}
            onInput={(val) => setSearchBy(val)}
          />}
        </div>
        <Input
          type="multiselect"
          label={searchMap?.[searchBy]}
          value={searchFilter}
          options={mobileLookup}
          onInput={(val) => setSearchFilter(val)}
          error={error.mobiles}
        />
        {searchFilter?.length > 1 && (
          <Infobox
            status="warning"
            type="inline"
            closable={false}
            message={(
              <>
                <strong>{`${t('Note')}: `}</strong>
                <span>
                  {t('you have selected two or more cars. If you want to see the speed history, you will have to select a single car.')}
                </span>
              </>
            )}
          />
        )}
        <Input
          label={t('Select a single or multiple dates')}
          type="daterange"
          value={dates}
          onInput={(val) => setDates(val)}
        />
        <Button
          type="primary"
          size="m"
          onClick={() => fetchHistory()}
          label={submitButtonLabel}
        />
      </div>
    </div>
  )
}))

export default History__sidebar
